import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import LeadPipelineCard from "../components/admin/LeadPipelineCard";

const FILTERS = [
  { key: "all",       label: "All" },
  { key: "new",       label: "New leads" },
  { key: "trial",     label: "Trial users" },
  { key: "customer",  label: "Customers" },
  { key: "churned",   label: "Former customers" },
  { key: "healthy",   label: "Healthy" },
  { key: "attention", label: "Needs attention" },
  { key: "critical",  label: "Critical" },
];

const SORTS = [
  { key: "newest",     label: "Newest first" },
  { key: "oldest",     label: "Oldest first" },
  { key: "score_desc", label: "Score (high → low)" },
  { key: "score_asc",  label: "Score (low → high)" },
  { key: "activity",   label: "Most recent activity" },
];

function StatusChip({ status }) {
  if (!status) return <span className="text-charcoal-400 text-xs">—</span>;
  const map = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red:   "bg-red-50 text-red-700 border-red-200",
  };
  const label = { green: "Healthy", amber: "Needs attention", red: "Critical" }[status] || status;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${map[status] || ''}`}>{label}</span>;
}

function LifecycleChip({ row }) {
  if (row.archived)     return <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border bg-charcoal-100 text-charcoal-500 border-black/10">Archived</span>;
  if (row.churned)      return <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border bg-charcoal-100 text-charcoal-600 border-black/10">Churned</span>;
  if (row.is_customer)  return <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border bg-emerald-100 text-emerald-800 border-emerald-200">Customer</span>;
  if (row.trial_started)return <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border bg-blue-50 text-blue-700 border-blue-200">Trial</span>;
  return                       <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border bg-charcoal-50 text-charcoal-700 border-black/10">New</span>;
}

export default function AdminLeads() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const active = params.get("filter") || "all";
  const sort = params.get("sort") || "newest";
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      // `needs_followup` isn't a mongo filter — fetch all, filter client-side.
      const backendFilter = active === "needs_followup" ? "all" : active;
      const url = `/admin/leads?filter=${backendFilter}&sort=${sort}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
      const r = await api.get(url);
      let leads = r.data.leads;
      if (active === "needs_followup") leads = leads.filter(l => l.needs_followup);
      setRows(leads);
    })().catch(() => setRows([]));
  }, [active, sort, q]);

  function setFilter(key) {
    const next = new URLSearchParams(params);
    next.set("filter", key);
    setParams(next);
  }
  function setSort(key) {
    const next = new URLSearchParams(params);
    next.set("sort", key);
    setParams(next);
  }

  const allFilters = FILTERS.concat([
    { key: "needs_followup", label: "Needs follow-up" },
    { key: "archived",       label: "Archived" },
  ]);

  return (
    <div className="min-h-screen bg-parchment">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">Owner dashboard</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tighter text-charcoal-900">Leads</h1>
            <p className="text-sm text-charcoal-500 mt-1">Every organization that requested a free Mission Watch report.</p>
          </div>
          <Link to="/admin/cost" data-testid="leads-back-to-owner" className="text-xs font-mono uppercase tracking-widest text-charcoal-500 hover:text-charcoal-900">← Back to Owner Dashboard</Link>
        </div>

        <div className="mb-6">
          <LeadPipelineCard />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="leads-filter-chips">
          {allFilters.map((f) => (
            <button key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`filter-${f.key}`}
                    className={`px-3 py-1.5 rounded-full border text-xs transition ${
                      active === f.key ? "bg-charcoal-900 text-white border-charcoal-900"
                                       : "bg-white text-charcoal-700 border-black/10 hover:border-black/20"}`}>
              {f.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <select value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid="leads-sort"
                    className="px-3 py-1.5 rounded-full border border-black/10 bg-white text-xs">
              {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="leads-search"
                   placeholder="Search org / website / email…"
                   className="px-3 py-1.5 rounded-full border border-black/10 bg-white text-xs w-72" />
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden" data-testid="leads-table">
          {rows === null ? (
            <p className="p-6 text-sm text-charcoal-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-charcoal-500">No leads match this filter.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-charcoal-50/60 text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
                <tr>
                  <th className="text-left px-4 py-3">Organization</th>
                  <th className="text-left px-4 py-3">Health</th>
                  <th className="text-left px-4 py-3">Score</th>
                  <th className="text-left px-4 py-3">Issues</th>
                  <th className="text-left px-4 py-3">Stage</th>
                  <th className="text-left px-4 py-3">Last activity</th>
                  <th className="text-left px-4 py-3">Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-t border-black/5 hover:bg-charcoal-50/40 cursor-pointer ${r.needs_followup ? "bg-amber-50/40" : ""} ${r.archived ? "opacity-60" : ""}`}
                      onClick={() => nav(`/admin/leads/${r.id}`)}
                      data-testid={`lead-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-charcoal-900">{r.organization_name || "—"}</div>
                      <div className="text-xs text-charcoal-500">{r.website}</div>
                      <div className="text-xs text-charcoal-400">{r.email}</div>
                    </td>
                    <td className="px-4 py-3"><StatusChip status={r.status} /></td>
                    <td className="px-4 py-3 font-mono">{r.score ?? "—"}</td>
                    <td className="px-4 py-3">{r.key_issues_count > 0 ? <span className="text-amber-700 font-medium">{r.key_issues_count}</span> : <span className="text-charcoal-400">—</span>}</td>
                    <td className="px-4 py-3"><LifecycleChip row={r} /></td>
                    <td className="px-4 py-3 text-xs text-charcoal-500 font-mono">{r.last_activity_at ? new Date(r.last_activity_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-xs text-charcoal-500">{r.notes_count || ""}</td>
                    <td className="px-4 py-3 text-right text-charcoal-400 text-lg">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
